// Vacation approval service for manager operations
// Implements API client for approval queue and team calendar (VAC-8)

import { get, post } from '@/api/lib/amplify-wrapper'
import { createImpersonatedApiHeaders, logApiCall } from './api-utils'
import type {
  ApprovalQueueResponse,
  CalendarResponse,
  VacationRequest,
  ApproveRequestPayload,
  RejectRequestPayload,
  VacationBalance
} from '@/types/vacation'

/**
 * Vacation Approval Service
 * Provides manager-specific vacation management operations
 */
export const VacationApprovalService = {
  /**
   * Get pending approval requests for authenticated manager
   * Uses /workforce/vacation/requests/pending-approvals endpoint
   */
  async getPendingApprovals(page = 1, pageSize = 20): Promise<ApprovalQueueResponse> {
    const path = '/workforce/vacation/requests/pending-approvals'
    logApiCall(path)

    const restOperation = get({
      apiName: 'monumenta',
      path,
      options: {
        headers: createImpersonatedApiHeaders(),
        queryParams: {
          page: page.toString(),
          pageSize: pageSize.toString()
        }
      }
    })

    const response = await restOperation.response
    const data = await response.body.json() as unknown
    return data as ApprovalQueueResponse
  },

  /**
   * Approve a vacation request
   * Uses /workforce/vacation/requests/{requestId}/approve endpoint
   */
  async approveRequest(
    requestId: string,
    payload: ApproveRequestPayload
  ): Promise<VacationRequest> {
    const path = `/workforce/vacation/requests/${requestId}/approve`
    logApiCall(path)

    const restOperation = post({
      apiName: 'monumenta',
      path,
      options: {
        headers: createImpersonatedApiHeaders(),
        body: payload as any
      }
    })

    const response = await restOperation.response
    const data = await response.body.json() as unknown
    return data as VacationRequest
  },

  /**
   * Reject a vacation request with required reason
   * Uses /workforce/vacation/requests/{requestId}/reject endpoint
   */
  async rejectRequest(
    requestId: string,
    payload: RejectRequestPayload
  ): Promise<VacationRequest> {
    const path = `/workforce/vacation/requests/${requestId}/reject`
    logApiCall(path)

    const restOperation = post({
      apiName: 'monumenta',
      path,
      options: {
        headers: createImpersonatedApiHeaders(),
        body: payload as any
      }
    })

    const response = await restOperation.response
    const data = await response.body.json() as unknown
    return data as VacationRequest
  },

  /**
   * Get vacation balance for a specific employee
   * Uses /workforce/vacation/balance endpoint with employeeId param
   */
  async getEmployeeBalance(employeeId: string): Promise<VacationBalance> {
    const path = '/workforce/vacation/balance'
    logApiCall(path)

    const restOperation = get({
      apiName: 'monumenta',
      path,
      options: {
        headers: createImpersonatedApiHeaders(),
        queryParams: { employeeId }
      }
    })

    const response = await restOperation.response
    const data = await response.body.json() as unknown
    return data as VacationBalance
  },

  /**
   * Get team vacation calendar for date range
   * Uses /workforce/vacation/calendar endpoint
   * @param startDate ISO 8601 date string (YYYY-MM-DD)
   * @param endDate ISO 8601 date string (YYYY-MM-DD)
   * @param employeeId Optional filter by specific employee
   * @param status Filter by status: APPROVED, PENDING, or ALL
   */
  async getTeamCalendar(
    startDate: string,
    endDate: string,
    employeeId?: string,
    status: 'APPROVED' | 'PENDING' | 'ALL' = 'APPROVED'
  ): Promise<CalendarResponse> {
    const path = '/workforce/vacation/calendar'
    logApiCall(path)

    const queryParams: Record<string, string> = {
      startDate,
      endDate,
      status
    }

    if (employeeId) {
      queryParams.employeeId = employeeId
    }

    const restOperation = get({
      apiName: 'monumenta',
      path,
      options: {
        headers: createImpersonatedApiHeaders(),
        queryParams
      }
    })

    const response = await restOperation.response
    const data = await response.body.json() as unknown
    return data as CalendarResponse
  }
}
